/**
 * The things a character can run out of, as data.
 *
 * `CharacterTemplate.healthModel` has always been an enum — `hit-points`,
 * `stress-track`, `wound-levels` — and nothing has ever read it. It is a label
 * on a box that is empty, so every system Machina runs has 5e's hit points
 * underneath whatever the label says. Tracks are the contents of that box.
 *
 * A track is a named number with a range, a direction, and named bands. That is
 * deliberately almost nothing, and it is still enough for the four shapes that
 * cover most of the hobby:
 *
 *   5e hit points   0..max (max is a FORMULA — it depends on the character),
 *                   starts full, harm subtracts, and 0 is a band with a name
 *   Trophy's Ruin   1..6, starts empty, harm ADDS, and 6 is the end of the
 *                   character — the same mechanic run backwards
 *   a stress track  0..4 boxes that fill, drawn as segments rather than a bar
 *   a doom counter  0..n, one-way, shared rather than personal
 *
 * The one that matters is Ruin. A system with no hit points still has a state
 * that degrades, and until a track could count UP toward a terminal band, the
 * only way to say "this character is used up" was to give them hit points and
 * pretend. Direction is what makes the same schema serve both.
 *
 * A fifth shape arrived with Trophy's Hunt Tokens: a number that fills and that
 * the player WANTS to fill. So which way a track runs (`direction`) and what
 * harm does to it (`harm`) are two fields, and harm can be `none`.
 *
 * WHAT A TRACK IS NOT: it does not tick, fire, spend, or remember. Nothing in
 * this file schedules anything or decides that something happens in the
 * fiction. Crossing into a band produces the band — a name, a description, and
 * a flag saying the author considers it terminal — and what the table does
 * about that is the table's business, or a Tier 3 code adapter's. Doomsworn's
 * Doom Clock advances on world ticks and forces a decision when it fills; that
 * is the reference example of a track-shaped thing that is NOT a track, and it
 * stays a code adapter. See ISS-0144.
 *
 * WHY BANDS RATHER THAN CODE: "bloodied", "the fourth Ruin", "the Doom Clock is
 * full" are the sentences a table actually says, and every one of them is a
 * comparison against a number plus a piece of authored prose. A system that
 * cannot declare them has to ship a component to render them, which is the
 * whole difference between Tier 2 and Tier 3.
 */
import { z } from 'zod';
import { evaluateFormula } from './expression';
// =============================================================================
// ERRORS
// =============================================================================
/**
 * A track definition cannot be made into numbers.
 *
 * Thrown rather than repaired, for the reason `FormulaError` gives: a track
 * that silently resolves to 0..0 is a character who is dead on creation and
 * nobody at the table can see why.
 */
export class TrackError extends Error {
    trackId;
    constructor(trackId, detail) {
        super(`Track "${trackId}": ${detail}`);
        this.trackId = trackId;
        this.name = 'TrackError';
    }
}
// =============================================================================
// SCHEMA
// =============================================================================
/**
 * A number, or a formula that produces one.
 *
 * Formulas go through `evaluateFormula` and nothing else — no `eval`, no
 * property access, no host objects. A 5e character's maximum hit points are
 * `8 + con_mod + (level - 1) * (5 + con_mod)`, which is arithmetic over values
 * the caller hands in, and arithmetic over caller-supplied values is the entire
 * capability a declarative system gets.
 */
export const TrackValueSchema = z.union([z.number(), z.string()]);
/**
 * Which way the number runs in play.
 *
 * A filling track counts up from its floor and a depleting one counts down
 * from its ceiling. That decides where a fresh track starts and which way its
 * bands open (see `TrackTierSchema.at`). It says nothing about whether moving
 * that way is bad: `harm` says that.
 *
 * It used to say both. The two values were documented as "higher is worse" and
 * "higher is healthier", and every track written before Trophy's Hunt Tokens
 * happened to agree on both facts. Hunt Tokens fill and that is good, so the
 * one field could not describe them (ISS-0155).
 */
export const TrackDirectionSchema = z.enum([
    /** Counts down from the top. Hit points, ammunition, Fate refresh. */
    'depleting',
    /** Counts up from the bottom. Ruin, stress, omens, Hunt Tokens, coin. */
    'filling',
]);
/**
 * What harm does to this track: raise it, lower it, or nothing at all.
 *
 * This is the field `applyTrackHarm` reads to decide the sign, so a consumer
 * that means "this character takes 3" never has to know which kind of track it
 * is holding. That is what replaces the hardcoded HP semantics.
 *
 * `none` is for a resource: Hunt Tokens, coin, supply, favour. Gaining and
 * spending those are ordinary moves, made with `applyTrackDelta`. Nobody is
 * harmed by being handed a token, so `applyTrackHarm` refuses such a track
 * rather than picking a sign for it.
 *
 * Optional, and absent means what `direction` used to mean: a filling track
 * is harmed upward and a depleting one downward. Every document written before
 * this field stated that pair in one word, so reading it back that way keeps
 * them behaving exactly as they did. A track that runs one way and is harmed
 * the other (Ironsworn's momentum fills, and a bad turn lowers it) says so.
 */
export const TrackHarmSchema = z.enum(['raises', 'lowers', 'none']);
/**
 * How the sheet should draw the track.
 *
 * These are three of the six kinds `SheetFieldKind` already has, named the
 * same, because a track has no rendering needs those six do not cover and a
 * seventh kind would be a widget every renderer then has to learn.
 *
 * Optional: absent means inferred from the range — bounded is a `meter`,
 * unbounded is a `score`. A system only says `clock` when it means discrete
 * boxes rather than a bar, which is a claim about the fiction (four stress
 * boxes are four things, 27 hit points are not twenty-seven things) and so has
 * to be authored rather than guessed.
 */
export const TrackDisplaySchema = z.enum(['meter', 'clock', 'score']);
/** Colour intent, matching `SheetTone` so a tier can hand its tone straight through. */
export const TrackToneSchema = z.enum(['neutral', 'warning', 'danger', 'arcane']);
/**
 * A named band on the track.
 *
 * `at` is the value where the band begins, read in the track's own direction:
 * a filling track enters a band at `at` and above, a depleting track at `at`
 * and below. Both are inclusive, because every rule that uses one of these is
 * written inclusively — "at 0 hit points", "when you mark your sixth Ruin".
 */
export const TrackTierSchema = z.object({
    id: z.string(),
    /** What the table calls it. "Bloodied", "Haunted", "Down". */
    name: z.string(),
    /**
     * Where the band begins, as a number or a formula. `min` and `max` are in
     * scope, so 5e's bloodied is literally `"max / 2"` rather than a number that
     * stops being right the moment the character levels.
     */
    at: TrackValueSchema,
    /**
     * What being in this band means, in the system's own words.
     *
     * This is where "you are unconscious and make death saving throws" lives.
     * It is a 5e rule, so it belongs in 5e's rules document — the runtime knows
     * that 0 is a band with a name and nothing else about it.
     */
    description: z.string().optional(),
    tone: TrackToneSchema.optional(),
    /**
     * The author considers this band the end of the character.
     *
     * A LABEL, NOT A TRIGGER. Nothing in this file acts on it; it is here so a
     * consumer can tell "Trophy's sixth Ruin" (the character is gone) apart from
     * "5e's zero hit points" (the character is having a bad round), which are the
     * same number in the same position on two tracks and mean opposite things.
     * Anything that reacts to it — killing the token, ending the session, taking
     * the sheet away — is that consumer's decision, not the schema's.
     */
    terminal: z.boolean().default(false),
});
/** A named resource with a range and named bands. */
export const TrackDefinitionSchema = z.object({
    id: z.string(),
    /** Shown on the sheet. "Hit Points", "Ruin", "Stress", "Doom". */
    name: z.string(),
    description: z.string().optional(),
    direction: TrackDirectionSchema,
    harm: TrackHarmSchema.optional(),
    /** Bottom of the range. Defaults to 0, which is where most of them sit. */
    min: TrackValueSchema.default(0),
    /**
     * Top of the range, if there is one.
     *
     * Optional because a plain counter — Debt, omens accrued, bodies buried —
     * genuinely has no ceiling, and giving it a fake one would make the sheet
     * draw a bar that is a lie about how close the character is to anything.
     */
    max: TrackValueSchema.optional(),
    /**
     * Where a fresh track sits. `min` and `max` are in scope.
     *
     * Defaults to the end the track runs from: a depleting track starts full, a
     * filling track starts empty. Systems where that is wrong — you begin play
     * already in debt — say so.
     */
    start: TrackValueSchema.optional(),
    display: TrackDisplaySchema.optional(),
    /**
     * Bands, in any order. The runtime sorts them; see `trackTierAt`.
     *
     * A track with no tiers is legal and means a number the system tracks but
     * attaches no meaning to.
     */
    tiers: z.array(TrackTierSchema).default([]),
    /**
     * The track belongs to the table rather than to a character — a doom clock,
     * a countdown to the raid.
     *
     * A flag rather than a separate schema, because a shared doom counter and a
     * personal misery counter differ only in who owns the number. Consumers use
     * it to decide where to store and draw it.
     */
    shared: z.boolean().default(false),
});
function resolveValue(trackId, what, value, scope) {
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new TrackError(trackId, `${what} is not a finite number`);
        return value;
    }
    try {
        return evaluateFormula(value, scope);
    }
    catch (err) {
        throw new TrackError(trackId, `${what} — ${err instanceof Error ? err.message : String(err)}`);
    }
}
/**
 * Turn a definition into numbers.
 *
 * `scope` is whatever the caller can offer the formulas: ability modifiers,
 * level, class hit die. A formula can see exactly that and nothing else.
 *
 * Resolution order is min, then max (which may reference `min`), then the
 * starting value and the tier thresholds (which may reference both). That
 * ordering is why 5e can write its bloodied band as `"max / 2"` and have it
 * stay correct at every level rather than being a number frozen at creation.
 *
 * Throws `TrackError` for a formula that cannot be evaluated or a range that is
 * inside out.
 */
export function resolveTrack(definition, scope = {}) {
    const { id } = definition;
    const min = resolveValue(id, 'min', definition.min, scope);
    const withMin = { ...scope, min };
    const max = definition.max === undefined ? undefined : resolveValue(id, 'max', definition.max, withMin);
    if (max !== undefined && max < min) {
        throw new TrackError(id, `max (${max}) is below min (${min})`);
    }
    const withRange = max === undefined ? withMin : { ...withMin, max };
    // The end the track runs FROM: full for depleting, empty for filling. That
    // is the healthy end of hit points and Ruin and also the right start for a
    // purse of Hunt Tokens, which is why it follows `direction` and not `harm`.
    // A depleting track with no maximum has nowhere full to start from, so it
    // starts at min and the system is expected to have said `start` if it meant
    // something else.
    const defaultStart = definition.direction === 'depleting' ? (max ?? min) : min;
    const start = definition.start === undefined
        ? defaultStart
        : resolveValue(id, 'start', definition.start, withRange);
    const tiers = definition.tiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        at: resolveValue(id, `tier "${tier.id}" threshold`, tier.at, withRange),
        description: tier.description,
        tone: tier.tone,
        terminal: tier.terminal,
    }));
    // Sorted so the band nearest the end the track runs toward comes first: a
    // filling track reads highest threshold down, a depleting track lowest
    // threshold up.
    //
    // WHY THE RUNTIME SORTS RATHER THAN THE AUTHOR, which is the opposite of what
    // `OutcomeLadder` does: a ladder rung's precedence cannot be derived from its
    // contents ("a natural 20 beats the DC" is a rule about order, not about
    // numbers), so the author states it. A track tier is a pure band on a single
    // number, so its precedence IS its threshold — deriving it removes a class of
    // authoring bug where a mis-ordered list silently reports the wrong band and
    // the sheet says "Healthy" at 2 hit points.
    tiers.sort((a, b) => (definition.direction === 'filling' ? b.at - a.at : a.at - b.at));
    return {
        id,
        name: definition.name,
        description: definition.description,
        direction: definition.direction,
        harm: definition.harm ?? (definition.direction === 'filling' ? 'raises' : 'lowers'),
        min,
        max,
        start,
        display: definition.display ?? (max === undefined ? 'score' : 'meter'),
        tiers,
        shared: definition.shared,
    };
}
/** A fresh track, at whatever the system says fresh means. */
export function initialTrackState(track) {
    return { trackId: track.id, value: clampTrackValue(track, track.start) };
}
// =============================================================================
// READING
// =============================================================================
/** Hold a value inside the declared range. An unbounded track clamps only below. */
export function clampTrackValue(track, value) {
    const floored = Math.max(value, track.min);
    return track.max === undefined ? floored : Math.min(floored, track.max);
}
/**
 * The band a value sits in, or null when the system declared none that reaches
 * it.
 *
 * Null rather than a default band, for the reason `interpretAgainstLadder`
 * returns null: a track whose author named only the bad end has not said the
 * character is fine, it has said nothing, and inventing "Healthy" would put a
 * word on the sheet the system never wrote.
 */
export function trackTierAt(track, value) {
    for (const tier of track.tiers) {
        const inBand = track.direction === 'filling' ? value >= tier.at : value <= tier.at;
        if (inBand)
            return tier;
    }
    return null;
}
/** The band the track is currently in. */
export function currentTrackTier(track, state) {
    return trackTierAt(track, state.value);
}
/** True when the track sits in a band its system marked as the end of the character. */
export function isTrackTerminal(track, state) {
    return trackTierAt(track, state.value)?.terminal ?? false;
}
/** Move a track by a signed amount on the raw number, clamped to its range. */
export function applyTrackDelta(track, state, delta) {
    const from = state.value;
    const requested = from + delta;
    const to = clampTrackValue(track, requested);
    const tierBefore = trackTierAt(track, from);
    const tierAfter = trackTierAt(track, to);
    return {
        state: { trackId: track.id, value: to },
        from,
        to,
        applied: to - from,
        clamped: to !== requested,
        tierBefore,
        tierAfter,
        changedTier: (tierBefore?.id ?? null) !== (tierAfter?.id ?? null),
    };
}
/**
 * Take `amount` of harm, in whichever direction this track calls harm.
 *
 * This is the function that pays for the whole schema. "The character takes 3"
 * subtracts on hit points and adds on Ruin, and the caller — a damage roll, a
 * failed save, a GM clicking a button — never learns which system it is in.
 * Every place that currently reaches for `currentHp` is a place that should
 * eventually call this instead.
 *
 * A negative `amount` relieves, so healing is harm run backwards; `relieveTrack`
 * is the same call with the sign flipped, for readability at the call site.
 *
 * Returns null on a track whose `harm` is `none`, the same refusal
 * `interpretAgainstLadder` makes when a ladder wants a target the roll does
 * not carry. Taking a Hunt Token away is spending it, not wounding anyone, and
 * a sign picked here would be a guess the system never made. A caller that
 * walks every track with one blow skips the null ones; a caller that means to
 * spend or gain uses `applyTrackDelta`.
 */
export function applyTrackHarm(track, state, amount) {
    switch (track.harm) {
        case 'raises':
            return applyTrackDelta(track, state, amount);
        case 'lowers':
            return applyTrackDelta(track, state, -amount);
        case 'none':
            return null;
    }
}
/**
 * Undo harm — healing, clearing stress, spending a session to walk one back.
 * Null on a track with no harm to undo, for the reason `applyTrackHarm` gives.
 */
export function relieveTrack(track, state, amount) {
    return applyTrackHarm(track, state, -amount);
}
/** Whether harm means anything on this track. False for a resource. */
export function trackTakesHarm(track) {
    return track.harm !== 'none';
}
/** Back to the starting value. A long rest, a new session, a fresh clock. */
export function resetTrack(track) {
    return initialTrackState(track);
}
// =============================================================================
// SHEET EMISSION
// =============================================================================
/**
 * Draw a track using the field kinds the sheet already has.
 *
 * The six in `SheetFieldKind` were enough, which is the point worth recording:
 * a bounded track is `meter` (current over max, the hit point bar that already
 * exists), a segmented one is `clock` (the widget built for Doomsworn's clock
 * renders a stress track unchanged), and an unbounded counter is `score`. No
 * seventh kind, so no renderer has to learn anything to draw a system nobody
 * has written yet.
 *
 * The current band's name and description ride along as `hint`, because that is
 * how a sheet says "Bloodied — you are below half" without the renderer knowing
 * either word.
 */
export function trackToSheetField(track, state) {
    const tier = trackTierAt(track, state.value);
    const hint = tier
        ? tier.description
            ? `${tier.name} — ${tier.description}`
            : tier.name
        : track.description;
    const field = {
        id: track.id,
        label: track.name,
        kind: track.display,
        value: state.value,
        tone: tier?.tone,
        hint,
    };
    // `score` is a bare number and carries no maximum; the other two are a
    // fraction and need one. An unbounded track cannot be either of those, so a
    // system asking for a meter without a max gets the number rather than a bar
    // divided by nothing.
    if (track.display !== 'score') {
        if (track.max === undefined) {
            field.kind = 'score';
        }
        else {
            field.max = track.max;
            // Carried only when it is not the usual zero, so a bar measures the span
            // the track actually occupies. Trophy's Ruin runs 1 to 6, and a meter
            // measured from zero draws a fresh character one-sixth doomed.
            if (track.min !== 0)
                field.min = track.min;
        }
    }
    return field;
}
/**
 * A whole set of tracks as one sheet section.
 *
 * States are looked up by track id; a track with no state is drawn at its
 * starting value, so a character created before the system added a track shows
 * the track full rather than at zero and dead.
 */
export function tracksToSheetSection(tracks, states, options = {}) {
    const byId = new Map(states.map((state) => [state.trackId, state]));
    return {
        id: options.id ?? 'tracks',
        title: options.title ?? 'Tracks',
        blurb: options.blurb,
        fields: tracks.map((track) => trackToSheetField(track, byId.get(track.id) ?? initialTrackState(track))),
        showWhenEmpty: true,
    };
}
// =============================================================================
// READY-MADE TRACKS
// =============================================================================
/**
 * The four shapes this schema was built to hold, as reference documents.
 *
 * These are declarations a SYSTEM would make, kept here the way `ladder.ts`
 * keeps `D20_ROLL_HIGH_LADDER` — as the thing the differential test in
 * ISS-0149 measures the runtime against, and as the worked example an author
 * copies. The 5e semantics in them ("you are unconscious", "below half is
 * bloodied") are 5e's, stated in 5e's document; nothing in this file reads
 * them.
 *
 * Each states `harm` outright even where the default from `direction` would
 * give the same answer, because authors copy these, and a copied track that
 * says what harm does cannot be misread when someone later flips its direction.
 */
/** 5e: 0..max, starts full, and 0 is a named band rather than a special case. */
export const HIT_POINTS_TRACK_5E = {
    id: 'hp',
    name: 'Hit Points',
    description: 'How much more you can take before you go down.',
    direction: 'depleting',
    harm: 'lowers',
    min: 0,
    // Average hit points for a class with a d10 hit die. A real 5e document
    // computes this from the class; the shape is what matters here.
    max: '8 + con_mod + (level - 1) * (6 + con_mod)',
    tiers: [
        {
            id: 'down',
            name: 'Down',
            at: 0,
            description: 'You are unconscious and make death saving throws.',
            tone: 'danger',
            terminal: false,
        },
        {
            id: 'bloodied',
            name: 'Bloodied',
            at: 'max / 2',
            description: 'At or below half your hit points.',
            tone: 'warning',
            terminal: false,
        },
        { id: 'healthy', name: 'Healthy', at: 'max', tone: 'neutral', terminal: false },
    ],
    shared: false,
};
/**
 * Trophy Dark: 1..6, counts UP, and the sixth Ruin is the end of the character.
 *
 * The proof the schema is not hit points in a hat. Same fields, opposite
 * direction, terminal band at the top instead of a survivable band at the
 * bottom, and no maximum-hit-point formula anywhere.
 */
export const RUIN_TRACK_TROPHY = {
    id: 'ruin',
    name: 'Ruin',
    description: 'What the forest has taken from you. It only goes up.',
    direction: 'filling',
    harm: 'raises',
    min: 1,
    max: 6,
    tiers: [
        {
            id: 'lost',
            name: 'Lost',
            at: 6,
            description: 'Your Ruin is 6. You are gone — the forest keeps you.',
            tone: 'danger',
            terminal: true,
        },
        {
            id: 'haunted',
            name: 'Haunted',
            at: 4,
            description: 'The forest is in you now.',
            tone: 'warning',
            terminal: false,
        },
        { id: 'marked', name: 'Marked', at: 1, tone: 'neutral', terminal: false },
    ],
    shared: false,
};
/** A Fate-style stress track: boxes that fill, drawn as segments rather than a bar. */
export const STRESS_TRACK = {
    id: 'stress',
    name: 'Stress',
    description: 'Boxes you can absorb a hit into. They clear when the scene ends.',
    direction: 'filling',
    harm: 'raises',
    min: 0,
    max: 4,
    display: 'clock',
    tiers: [
        {
            id: 'overwhelmed',
            name: 'Overwhelmed',
            at: 4,
            description: 'Every box is full. The next hit takes a consequence.',
            tone: 'danger',
            terminal: false,
        },
        {
            id: 'strained',
            name: 'Strained',
            at: 2,
            tone: 'warning',
            terminal: false,
        },
        { id: 'steady', name: 'Steady', at: 0, tone: 'neutral', terminal: false },
    ],
    shared: false,
};
/**
 * A shared omen counter: one-way, belongs to the table, no character owns it.
 *
 * Note what it is NOT. It has no schedule, no trigger, and nothing that happens
 * when it fills beyond a band with a description a GM reads aloud. A counter
 * that advanced itself on the world tick, or that stopped play to demand a
 * decision, would be Doomsworn's Doom Clock and would be a code adapter.
 */
export const OMEN_TRACK = {
    id: 'omens',
    name: 'Omens',
    description: 'What the table has seen coming. It does not go back.',
    direction: 'filling',
    harm: 'raises',
    min: 0,
    max: 8,
    display: 'clock',
    shared: true,
    tiers: [
        {
            id: 'arrived',
            name: 'It Is Here',
            at: 8,
            description: 'The omens are complete.',
            tone: 'danger',
            terminal: false,
        },
        { id: 'gathering', name: 'Gathering', at: 4, tone: 'warning', terminal: false },
        { id: 'quiet', name: 'Quiet', at: 0, tone: 'neutral', terminal: false },
    ],
};
//# sourceMappingURL=tracks.js.map